import { State } from "../index";
import { createSelector } from "reselect";
import { getDataFor, getForm, getPatchedData } from "./formSelectors";
import {
	ElementData,
	BoundElement,
	LegacyArrayViewElement,
} from "records/Element";
import _ from "lodash";
import { FormDataValue, QueryClause, SearchConfig } from "lib/api/types";
import { FormData } from "records/Form";

import pointer from "json-pointer";
import { parseLabel } from "../../elements/utils";
import jsonpointer from "lib/jsonpointer/jsonpointer";

interface DataSelectorProps {
	fid: string;
	vid: string;
	rowIndex: number;
	id: string;
	index?: number;
	indexes?: number[];
	uuid: string;
}

type ElementSelectorProps = { fid: string; uuid: string };

export type ElementComponentData<V = FormDataValue | undefined> = Omit<
	ElementData,
	"value" | "feedback"
> & {
	status?: "valid" | "error";
	value: V;
	feedback?: string[];
};

export type ListElementComponentData<V = string | number | boolean | null> =
	ElementComponentData<V[]> & {
		items: {
			id: string;
			value: Required<V>;
			label: string;
			feedback?: string[];
			status?: "valid" | "error";
		}[];
	};

function createComponentData(id: string, uuid: string, data?: ElementData) {
	let output: ElementComponentData = {
		id,
		status: "valid",
		value: undefined,
	};

	if (data) {
		// If we have data split feedback
		const { feedback, label, ...rest } = data;
		// Add the rest of the data items
		output = { ...output, ...rest };

		if (label) output.label = parseLabel(label);

		// Get the feedback for our particular element and add it to the output
		if (feedback && feedback[uuid]) {
			output.feedback = feedback[uuid];
		}
	}

	// If we have any feedback, assume it's an error and set the status
	if (output.feedback && output.feedback.length > 0) output.status = "error";

	return output;
}

/**
 * Takes an array of uuid's and returns an array of tuples containg
 *  the uuid and the corresponding data id of the element [[uuid, id], [uuid, id]...]
 * @param state redux store
 * @param fid form id
 * @param uuids array of uuids
 */
export type TraversalWithIds = string[][];
export const getIds = (state: State, fid: string, uuids: string[]) => {
	return uuids.map(uuid => {
		const id = (getElement(state, { fid, uuid }) as BoundElement).id;
		return [uuid, id || ""];
	});
};

/**
 * Get the entity key of the data that a view is bound to
 * Views that currently have no data will have a binding of
 * `null`. Where this is the case the `vid` is used instead
 */
export const getBinding = (
	state: State,
	{ fid, vid }: { fid: string; vid: string }
) => {
	const binding = getForm(state, { fid }).server.bindings[vid];
	// If the binding is set to null then we temporarily use the vid
	return binding || vid;
};

/**
 * Get an Element record from state
 */
export const getElement = (state: State, props: ElementSelectorProps) =>
	getForm(state, props)?.server.elements[props.uuid];

export const getElementByName = (state: State, fid: string, name: string) => {
	const elements = getForm(state, { fid })?.server.elements;
	if (!elements) return;
	return Object.values(elements).find(e => e.name === name);
};

export const getElementIdByName = (state: State, fid: string, name: string) =>
	getElementByName(state, fid, name)?.id;

export const prepSearchConfig = (
	state: State,
	props: Pick<DataSelectorProps, "fid" | "vid" | "rowIndex">,
	search: SearchConfig
) => {
	// Clone search object so we can mutate the copy
	const searchObj = _.cloneDeep(search);

	// Fix up sort array - it arrives as JSON pointers but we need it to use dot notation
	if (searchObj.payload.sort && searchObj.payload.sort.length > 0) {
		searchObj.payload.sort.forEach((v, i) => {
			const item = searchObj.payload.sort[i];
			if (item.field) item.field = jsonpointer.pointerToId(item.field);
		});
	}

	// No predefined filters - just return the search object
	if (searchObj.payload.filter.AND.length === 0) return searchObj;

	// Get the binding of the element for retrieving data for the filters
	const binding = getBinding(state, props);

	// Create a json pointer dictionary that we can iterate through
	const jsonDict = pointer.dict(search.payload.filter.AND) as {
		[k: string]: string | number | null | boolean;
	};
	// Iterate over the dictionary
	pointer.walk(jsonDict, (v, k) => {
		if (typeof v !== "string") return;

		// Check if the filter has a dynamic value
		const match = v.match(/\$\{(.+?)\}/);

		// If it's just a regular filter, continue
		if (!match || match.length < 1) return;
		const name = match[1];

		// Get the element that the filter is targeting
		const filterElem = getElementByName(state, props.fid, name);

		// Check that the element has an id binding it to data
		if (filterElem?.id !== undefined) {
			// Get the data for the dynamic filter
			const data = getData(state, {
				binding,
				...props,
				id: filterElem.id,
			});

			// If the data contains a value then push
			// it in to the query, replacing the dynamic
			// filter placeholder
			if (data && data.value !== undefined) {
				pointer.set(jsonDict, k, data.value);
			} else {
				// If there is no value then remove
				// the filter from the query
				pointer.remove(jsonDict, k);
			}
		} else {
			// If we have no ID then remove the filter from
			// the query
			pointer.remove(jsonDict, k);
		}
	});

	// Build up our new filters
	const filters: QueryClause = [];
	Object.keys(jsonDict).forEach(v => {
		_.set(filters, pointer.parse(v), jsonDict[v]);
	});

	// Add the new array of filters to the new search object
	searchObj.payload.filter.AND = filters;

	return searchObj;
};

/**
 * Get the elem type of an Element, "text", "select" etc..
 */
export const getElementType = (state: State, props: ElementSelectorProps) =>
	getElement(state, props)?.elem;

/**
 * Get the element label
 *
 * @param state Redux store
 * @param props { fid and uuid }
 * @returns label of uuid element
 */
export const getElementLabel = (state: State, props: ElementSelectorProps) =>
	getElement(state, props)?.label;

/**
 * Get the element id
 *
 * @param state Redux store
 * @param props { fid and uuid }
 * @returns id of uuid element
 */
export const getElementId = (state: State, props: ElementSelectorProps) =>
	getElement(state, props)?.id;

/**
 * Get label and id for array of uuids
 *
 * @param state Redux store
 * @param fid form id
 * @param uuids array of uuids
 * @returns array of [[label, id]..]
 */
export const getLabelsAndIdsForElements = (
	state: State,
	fid: string,
	uuids: string[]
) =>
	uuids.map(uuid => [
		getElementLabel(state, { fid, uuid }),
		getElementId(state, { fid, uuid }),
	]);

/**
 * Get the UUID of an element using the elements name property
 * @param state Redux store
 * @param fid form instance id
 * @param name the name of the element
 * @returns the uuid of the matching element
 */
// TODO: unit tests
export const getUUIDByName = (state: State, fid: string, name: string) => {
	const elements = getForm(state, { fid })?.server.elements;
	if (elements)
		return Object.keys(elements).find(uuid => elements[uuid].name === name);
};

export const createGetElementListData = () =>
	createSelector(
		getPatchedData,
		getBinding,
		getElementListData,
		(_, props: DataSelectorProps) => props,
		(data, binding, _items, props) => {
			const elementData = getElementData(data, binding, props);
			return {
				id: props.id,
				items: _items
					? _items.map(item => ({
							...item,
							label: parseLabel(item.label),
					  }))
					: [],
				value: elementData.value,
				label: elementData.label,
				feedback: elementData.feedback,
				status: elementData.status,
			} as ListElementComponentData;
		}
	);

/**
 * Get element data for a component that renders a list of items,
 * searches through all data and finds all data rows for the element
 *
 * For some elements such as the Reference element multiple values
 * are returned in a json-pointer format, this means that the data
 * is separated into multiple keyed objects rather than just held
 * under the key of the element we're trying to render
 *
 * @example
 *  "data": {
 *    "myRefField": { value: ['someRef', 'anotherRef'] },
 *    "myRefField/0": { value: 'someRef', label: 'Reference A' },
 *    "myRefField/1": { value: 'anotherRef', label: 'Reference B' }
 *  }
 */
// const getElementListData = (
// 	state: State,
// 	{ fid, vid, rowIndex, id }: DataSelectorProps
// ) => {
// 	const binding = getBinding(state, { fid, vid });
// 	if (!binding) return;

// 	const data = getPatchedData(state, { fid })[binding][rowIndex];

// 	// Count the number of keys that match the ${id}/${index} pattern
// 	const listLength = _.keys(
// 		_.pickBy(data, (v, k) => _.startsWith(k, `${id}/`))
// 	).length;

// 	// const elementData = data[id] as ElementData<string[]>;
// 	// const listLength = elementData?.value?.length || 0;
// 	const listData = [] as ElementData<FormDataValue>[];

// 	for (let index = 0; index < listLength; index++) {
// 		const indexValue = data[`${id}/${index}`];
// 		if (indexValue !== undefined) listData.push(indexValue);
// 	}
// 	return listData;
// };

const getElementListData = (
	state: State,
	{ fid, vid, rowIndex, id, uuid }: DataSelectorProps
) => {
	const binding = getBinding(state, { fid, vid });
	if (!binding) return;
	const data = getPatchedData(state, { fid })[binding][rowIndex];
	// Count the number of keys that match the ${id}/${index} pattern
	const listLength = _.keys(
		_.pickBy(data, (v, k) => _.startsWith(k, `${id}/`))
	).length;

	const listData = [] as ElementComponentData<FormDataValue | undefined>[];

	for (let index = 0; index < listLength; index++) {
		const rowData = data[`${id}/${index}`];

		// Skip any rows marked for deletion
		if (rowData?._deleted) continue;

		const indexValue = createComponentData(id, uuid, rowData);
		if (indexValue !== undefined) listData.push(indexValue);
	}
	return listData;
};

const getData = (
	state: State,
	props: Pick<DataSelectorProps, "fid" | "id" | "rowIndex"> & {
		binding: string;
	}
) => {
	const data = getDataFor(state, props);
	if (data && data[props.rowIndex]) {
		return data[props.rowIndex][props.id];
	}
};

/**
 * Create a selector that will return element data when called.
 * The returned data has any data patches applied.
 *
 * An `undefined` value in the returned object is an indication
 * that the element has no data.
 *
 * @example
 * { id: "my-element", value: "abc123", label: "My element" }
 */

const getElementData = (
	formData: FormData,
	binding: string,
	props: DataSelectorProps
) => {
	if (props.indexes) {
		const indexesData: ElementComponentData<FormDataValue | undefined>[] =
			[];
		props.indexes.forEach((index: number) => {
			const id = index !== undefined ? `${props.id}/${index}` : props.id;

			const result = createComponentData(
				id,
				props.uuid,
				formData?.[binding]?.[props.rowIndex]?.[id]
			);
			indexesData.push(result);
		});
		if (indexesData[0]?.hasOwnProperty("label")) {
			if (
				new Set(
					indexesData.map(
						(
							data: ElementComponentData<
								FormDataValue | undefined
							>
						) => data.label
					)
				).size !== 1
			) {
				indexesData[0].label = "";
			}
		} else {
			if (
				new Set(
					indexesData.map(
						(
							data: ElementComponentData<
								FormDataValue | undefined
							>
						) => data.value
					)
				).size !== 1
			) {
				indexesData[0].value = "";
			}
		}

		return indexesData[0];
	}
	const id =
		props.index !== undefined ? `${props.id}/${props.index}` : props.id;
	const data = createComponentData(
		id,
		props.uuid,
		formData?.[binding]?.[props.rowIndex]?.[id]
	);
	return data;
};

export const createGetElementData = () =>
	createSelector(
		getPatchedData,
		getBinding,
		(_, props: DataSelectorProps) => props,
		(formData, binding, props): ElementComponentData =>
			getElementData(formData, binding, props)
	);

/**
 * Returns API data for an element if it exists, this does not
 * apply any json patches that are currently been held to the
 * returned data
 *
 * @example
 * { id: "my-element", value: "abc123", label: "My element" }
 */
export const getOrigElementData = (
	state: State,
	{ fid, vid, rowIndex, id, index, uuid }: DataSelectorProps
): ElementComponentData | undefined => {
	const binding = getBinding(state, { fid, vid });
	if (!binding) return undefined;
	const tmpId = index !== undefined ? `${id}/${index}` : id;
	return createComponentData(
		tmpId,
		uuid,
		state.forms[fid]?.server.data[binding]?.[rowIndex]?.[tmpId]
	);
};

/**
 *
 * @param state
 * @param fid
 * @param uuid
 * @param rowIndex
 */
// TODO: Memoize
export const getLegacyArrayViewData = (
	state: State,
	fid: string,
	vid: string,
	uuid: string,
	rowIndex = 0
) => {
	const element = getElement(state, { fid, uuid }) as LegacyArrayViewElement;

	// Grab the views traversal
	const binding = getBinding(state, { fid, vid });

	// Get patched data
	const viewData = getPatchedData(state, { fid })[binding]?.[rowIndex];
	const rows: { [k: string]: ElementComponentData }[] = [];
	const ids = getIds(state, fid, element.traversal);

	if (viewData) {
		// Create our rows
		ids?.forEach(item => {
			const regExp = new RegExp(`${item[1]}/(\\d+)`);
			const data = _.pickBy(viewData, (v, k) => regExp.test(k));

			Object.keys(data).forEach(v => {
				const match = regExp.exec(v);
				if (match && match[1]) {
					const index = Number(match[1]);

					if (rows[index] === undefined) rows[index] = {};

					rows[index][item[1]] = createComponentData(
						item[1],
						item[2],
						data[v]
					);
				}
			});
		});
	}

	return rows;
};
